import React, { useState } from 'lib/react';
import {
  EVTable, EVTableBody, EVTableCell, EVTableHead, EVTableRow, makeStyles,
  EVKeyboardArrowDown,
  EVIconButton,
  EVGrid,
} from 'lib/components';
import { PropTypes } from 'prop-types';
import clsx from 'clsx';
import { hasExpandableContent } from 'utils/app.utils';

const useStyles = makeStyles((theme) => ({
  arrowBtnDown: {
    alignContent: 'center',
    height: '44px',
    width: '44px',
    color: theme.evPalette.ocean[600],
    transition: 'background-color 0.5s ease, transform 0.5s ease',
    '&.arrowBtnUp': {
      borderRadius: '4px',
      backgroundColor: `${theme.evPalette.ocean[600]} !important`,
      color: theme.evPalette.salt[100],
      transform: 'rotate(-180deg)',
    },
  },
  expandBtn: {
    cursor: 'pointer',
    padding: '0px',
  },
  tableRowEven: {
    backgroundColor: `${theme.evPalette.charcoal[100]} !important`,
  },
  tableRowOdd: {
    backgroundColor: `${theme.evPalette.salt[100]} !important`,
  },
  tableRowExpanded: {
    borderLeft: `3px solid ${theme.evPalette.ocean[600]} !important`,
  },
  unExpandedRow: {
    padding: '0px',
    transition: 'padding 0.5s ease',
    '&.expanded': {
      padding: '24px',
    },
  },
  expandedRowContent: {
    display: 'flex',
    flexWrap: 'wrap',
    width: 'fit-content',
    gap: '24px',
    paddingLeft: '8px',
  },
}));
function VirtualizedTable(props) {
  const {
    columns, dataList, ExpandedRowComponent, rowKey, headerProps, bodyProps,
    noResultsMessage, expandedRowFlags, onExpandedRowFlagsChange, ...rest
  } = props;
  const classes = useStyles();
  const [expandedRowFlagsState, setExpandedRowFlags] = useState({});

  const handleExpandedRowVisibility = (key) => {
    if (onExpandedRowFlagsChange) return onExpandedRowFlagsChange(key);
    return setExpandedRowFlags((prevState) => ({ ...prevState, key: !prevState[key] }));
  };
  const expandedRows = expandedRowFlags || expandedRowFlagsState;

  return (
    <EVTable stickyHeader {...rest}>
      <EVTableHead {...headerProps}>
        <EVTableRow>
          {ExpandedRowComponent && <EVTableCell />}
          {columns.map(({ label }) => (
            <EVTableCell key={`${label}-head`}>
              {label}
            </EVTableCell>
          ))}
        </EVTableRow>
      </EVTableHead>
      <EVTableBody {...bodyProps}>
        {(dataList?.length > 0) ? dataList.map((rowData, rowIndex) => (
          <>
            <EVTableRow
              key={`row-${rowData[rowKey]}`}
              className={clsx(
                classes[`tableRow${rowIndex % 2 === 0 ? 'Even' : 'Odd'}`],
                expandedRows[rowData[rowKey]] && hasExpandableContent(rowData.expandedRow) && classes.tableRowExpanded,
              )}
            >
              {ExpandedRowComponent && (
              <EVTableCell>
                {hasExpandableContent(rowData.expandedRow) && (
                <EVIconButton
                  id={`expand-btn-${rowData[rowKey]}`}
                  className={classes.expandBtn}
                  onClick={() => handleExpandedRowVisibility(rowData[rowKey])}
                >
                  <EVGrid className={clsx(classes.arrowBtnDown, expandedRows[rowData[rowKey]] && 'arrowBtnUp')}>
                    <EVKeyboardArrowDown />
                  </EVGrid>
                </EVIconButton>
                )}
              </EVTableCell>
              )}
              {columns.map(({ dataKey, cellProps = {} }) => (
                <EVTableCell key={dataKey} {...cellProps}>
                  {rowData[dataKey]}
                </EVTableCell>
              ))}
            </EVTableRow>
            {hasExpandableContent(rowData.expandedRow) && ExpandedRowComponent && (
              <EVTableRow
                key={`expanded-row-${rowData[rowData[rowKey]]}`}
                className={clsx(
                  classes[`tableRow${rowIndex % 2 === 0 ? 'Even' : 'Odd'}`],
                  expandedRows[rowData[rowKey]] && hasExpandableContent(rowData.expandedRow) && classes.tableRowExpanded,
                )}
              >
                <EVTableCell
                  colspan={columns.length + 1}
                  className={clsx(classes.unExpandedRow, expandedRows[rowData[rowKey]] && 'expanded')}
                >
                  <EVGrid className={classes.expandedRowContent}>
                    {rowData.expandedRow && expandedRows[rowData[rowKey]]
                    && <ExpandedRowComponent expandedRowData={rowData.expandedRow} />}
                  </EVGrid>
                </EVTableCell>
              </EVTableRow>
            )}
          </>
        ))
          : (
            <EVTableRow className="no-results">
              <EVTableCell>{noResultsMessage}</EVTableCell>
            </EVTableRow>
          )}
      </EVTableBody>
    </EVTable>
  );
}

VirtualizedTable.defaultProps = {
  headerProps: {},
  bodyProps: {},
  noResultsMessage: 'No Results',
  expandedRowData: null,
  ExpandedRowComponent: null,
  expandedRowFlags: {},
  onExpandedRowFlagsChange: null,
};

VirtualizedTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    dataKey: PropTypes.string.isRequired,
    cellProps: PropTypes.shape({}),
  })).isRequired,
  dataList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  rowKey: PropTypes.string.isRequired,
  headerProps: PropTypes.shape(),
  bodyProps: PropTypes.shape(),
  noResultsMessage: PropTypes.string,
  expandedRowData: PropTypes.arrayOf(PropTypes.shape()),
  ExpandedRowComponent: PropTypes.func,
  expandedRowFlags: PropTypes.arrayOf(PropTypes.bool),
  onExpandedRowFlagsChange: PropTypes.func,
};

export default VirtualizedTable;
